import { useState, useEffect, useRef, useCallback } from 'react';
import { studentService } from '../services/apiService';
import StudentRequestList from '../components/StudentRequestList';
import * as XLSX from 'xlsx';

const StudentRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    count: 0,
    totalRecords: 0
  });
  const [downloading, setDownloading] = useState(false);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchRef = useRef(null);

  const fetchRequests = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      
      const response = await studentService.getAllStudents({
        status: filter,
        page,
        limit: 10,
        search: search.trim()
      });
      
      setRequests(response.data || []);
      setPagination(response.pagination || {
        current: 1,
        total: 0,
        count: 0,
        totalRecords: 0
      });
    } catch (err) {
      console.error('Error fetching student requests:', err);
      setError(err.message || 'Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  // Function to download approved students as Excel
  const downloadApprovedStudents = async () => {
    try {
      setDownloading(true);
      // Fetch all approved students without pagination
      const response = await studentService.getAllStudents({
        status: 'approved',
        page: 1,
        limit: 10000,
        search: ''
      });
      
      const approvedStudents = response.data || [];
      
      if (approvedStudents.length === 0) {
        alert('No approved students found to download');
        return;
      }
      
      // Prepare data for Excel
      const dataToExport = approvedStudents.map(student => ({
        'Name': student.name,
        'Email': student.email,
        'Phone': student.phone,
        'Institute': student.instituteName,
        'City': student.city,
        'State': student.state,
        'Country': student.country,
        'Enrollment Number': student.enrollmentNumber,
        'Course': student.course,
        'Year': student.year,
        'Status': student.isActive ? 'Active' : 'Inactive',
        'Approval Date': student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : 'N/A',
        'Admin Notes': student.adminNotes || 'N/A'
      }));
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Approved Students');
      
      // Generate Excel file and trigger download
      const fileName = `Approved_Students_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (err) {
      console.error('Error downloading students:', err);
      setError('Failed to download approved students');
    } finally {
      setDownloading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await studentService.getAllStudents({
        status: 'all',
        page: 1,
        limit: 10,
        search: searchQuery.trim()
      });
      
      setSearchResults(response.data || []);
      setShowSearchDropdown(true);
    } catch (err) {
      console.error('Error searching students:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 300);
  };

  // Handle selecting a search result
  const handleSelectSearchResult = (student) => {
    setSearchTerm(student.name);
    setShowSearchDropdown(false);
    fetchRequests(1, student.name);
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchDropdown(false);
    fetchRequests(1, '');
  };

  // Click outside handler for search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchRequests(1, searchTerm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleApprove = async (id, adminNotes = '') => {
    try {
      await studentService.approveStudent(id, { adminNotes });
      fetchRequests(pagination.current, searchTerm);
    } catch (err) {
      console.error('Error approving student:', err);
      setError(err.message || 'Failed to approve student');
    }
  };

  const handleReject = async (id, rejectionReason, adminNotes = '') => {
    try {
      await studentService.rejectStudent(id, { rejectionReason, adminNotes });
      fetchRequests(pagination.current, searchTerm);
    } catch (err) {
      console.error('Error rejecting student:', err);
      setError(err.message || 'Failed to reject student');
    }
  };

  const handleToggleStatus = async (id, isActive, adminNotes = '') => {
    try {
      await studentService.toggleStudentStatus(id, { isActive, adminNotes });
      fetchRequests(pagination.current, searchTerm);
    } catch (err) {
      console.error('Error toggling student status:', err);
      setError(err.message || 'Failed to update student status');
    }
  };

  const getStatusBadge = (request) => {
    if (request.isVerified) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Approved</span>;
    } else if (request.rejectionReason) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
    }
  };

  const getImageSrc = (imageData) => {
    if (!imageData) return null;
    if (imageData.startsWith('http') || imageData.startsWith('data:')) {
      return imageData;
    }
    return `data:image/jpeg;base64,${imageData}`;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-medium text-gray-800">Youth Ambassador Requests</h2>
        {filter === 'approved' && (
          <button
            onClick={downloadApprovedStudents}
            disabled={downloading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            {downloading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Preparing Download...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Approved Students
              </>
            )}
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative" ref={searchRef}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search students by name, email, institute, or city..."
            className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-[#0e3d1f] focus:border-[#0e3d1f] text-sm"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600"
            >
              <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          {searchLoading && (
            <div className="absolute inset-y-0 right-8 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0e3d1f]"></div>
            </div>
          )}
        </div>

        {/* Search Dropdown */}
        {showSearchDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {searchLoading ? (
              <div className="relative select-none py-3 px-4 text-gray-700 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0e3d1f] mr-2"></div>
                Searching students...
              </div>
            ) : searchResults.length === 0 ? (
              <div className="relative select-none py-3 px-4 text-gray-500 text-center">
                <svg className="mx-auto h-6 w-6 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.469-.935-6.02-2.454M15.544 13.544a4 4 0 00-7.088 0M6.2 15.6L4.8 12m10.4 3.6l1.4-3.6" />
                </svg>
                No students found for "{searchTerm}"
              </div>
            ) : (
              <>
                <div className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                </div>
                {searchResults.map((student) => (
                  <div
                    key={student._id}
                    onClick={() => handleSelectSearchResult(student)}
                    className="relative cursor-pointer select-none py-2 px-4 hover:bg-[#0e3d1f] hover:text-white transition-colors group"
                  >
                    <div className="flex items-center">
                      {student.profilePicture ? (
                        <img 
                          src={getImageSrc(student.profilePicture)} 
                          alt={`${student.name} profile`}
                          className="w-8 h-8 rounded-full mr-3 object-cover border group-hover:border-white"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full mr-3 bg-gray-200 flex items-center justify-center group-hover:bg-white group-hover:text-gray-800">
                          <span className="text-xs font-medium">
                            {student.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{student.name}</div>
                        <div className="text-sm opacity-75 truncate">{student.instituteName} • {student.city}</div>
                      </div>
                      <div className="ml-2 flex-shrink-0">
                        {getStatusBadge(student)}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Search Results Info */}
      {searchTerm && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <span className="text-blue-800 text-sm">
                Search results for "<strong>{searchTerm}</strong>" in {filter} students
              </span>
            </div>
            <button
              onClick={handleClearSearch}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear search
            </button>
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="mb-6 flex justify-center">
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-[#0e3d1f] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
            }`}
          >
            Pending ({pagination.totalRecords || 0})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'approved'
                ? 'bg-[#0e3d1f] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'rejected'
                ? 'bg-[#0e3d1f] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
            }`}
          >
            Rejected
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'active'
                ? 'bg-[#0e3d1f] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'inactive'
                ? 'bg-[#0e3d1f] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
            }`}
          >
            Inactive
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0e3d1f]"></div>
          <p className="mt-2 text-gray-600">Loading requests...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button 
                onClick={() => fetchRequests(pagination.current)}
                className="mt-2 text-sm text-red-600 underline hover:text-red-800"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : (
        <StudentRequestList 
          requests={requests}
          onApprove={handleApprove}
          onReject={handleReject}
          onToggleStatus={handleToggleStatus}
          pagination={pagination}
          onPageChange={(page) => fetchRequests(page, searchTerm)}
          currentFilter={filter}
        />
      )}
    </div>
  );
};

export default StudentRequests;